/**
 * authClient.js — Axios Instances + Interceptors + Refresh Queue
 *
 * สร้าง axios instance แยก 2 ตัว:
 *   - authClient  → /auth/* (login, refresh, logout, heartbeat) — ไม่มี 401 retry
 *   - apiClient   → /api/*  (ทุก API call) — มี 401 retry + refresh queue
 *
 * Refresh Queue (module-level singleton):
 *   ป้องกัน request หลายตัวที่ 401 พร้อมกัน ไม่ต่างคน refresh ของตัวเอง
 *   → คนแรก refresh, คนอื่นรอใน queue แล้วรับ token ใหม่พร้อมกัน
 */

import axios from 'axios';
import { AUTH_BASE_URL, API_BASE_URL, PUBLIC_BASE_URL } from '../config/api';

// ─── Axios Instances ─────────────────────────────────────────────────────────

export const authClient = axios.create({ baseURL: AUTH_BASE_URL });
export const apiClient  = axios.create({ baseURL: API_BASE_URL });
export const publicClient = axios.create({ baseURL: PUBLIC_BASE_URL });

// ─── Cross-Tab Refresh Lock ─────────────────────────────────────────────────

const AUTH_CHANNEL_NAME = 'kitcrm_auth';
const REFRESH_LOCK_KEY = 'kitcrm_refresh_lock';
const REFRESH_LOCK_TTL = 15_000;
const REFRESH_WAIT_TIMEOUT = 20_000;
const REFRESH_WAIT_POLL = 250;

const getTabRefreshOwner = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const readRefreshLock = () => {
  try {
    const raw = localStorage.getItem(REFRESH_LOCK_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const isRefreshLockActive = (lock) =>
  lock?.owner && Number(lock.expiresAt || 0) > Date.now();

const clearRefreshLock = (owner) => {
  const lock = readRefreshLock();
  if (!lock || lock.owner === owner) {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  }
};

const waitForCrossTabRefresh = (previousRefreshToken) => new Promise((resolve, reject) => {
  let done = false;
  let channel = null;
  let poll = null;
  let timeout = null;

  const cleanup = () => {
    done = true;
    if (timeout) clearTimeout(timeout);
    if (poll) clearInterval(poll);
    window.removeEventListener('storage', checkStoredTokens);
    if (channel) channel.close();
  };

  const resolveFromStorage = () => {
    const accessToken = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    if (!accessToken || !refreshToken || refreshToken === previousRefreshToken) return false;

    cleanup();
    resolve({
      data: {
        access_token: accessToken,
        refresh_token: refreshToken,
      },
    });
    return true;
  };

  const checkStoredTokens = () => {
    if (done) return;
    resolveFromStorage();
  };

  if ('BroadcastChannel' in window) {
    channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    channel.onmessage = ({ data }) => {
      if (done || !data?.type) return;
      if (data.type === 'TOKEN_REFRESHED') {
        resolveFromStorage();
      }
      if (data.type === 'LOGGED_OUT') {
        cleanup();
        reject(new Error('Session logged out in another tab'));
      }
    };
  }

  window.addEventListener('storage', checkStoredTokens);

  poll = setInterval(checkStoredTokens, REFRESH_WAIT_POLL);
  timeout = setTimeout(() => {
    cleanup();
    reject(new Error('Cross-tab refresh timeout'));
  }, REFRESH_WAIT_TIMEOUT);

  checkStoredTokens();
});

export const refreshWithCrossTabLock = async (refreshToken) => {
  const owner = getTabRefreshOwner();
  const existingLock = readRefreshLock();

  if (isRefreshLockActive(existingLock)) {
    return waitForCrossTabRefresh(refreshToken);
  }

  localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify({
    owner,
    expiresAt: Date.now() + REFRESH_LOCK_TTL,
  }));

  const lock = readRefreshLock();
  if (lock?.owner !== owner) {
    return waitForCrossTabRefresh(refreshToken);
  }

  try {
    const response = await authClient.post('/refresh', { refresh_token: refreshToken });
    const { access_token, refresh_token: newRefreshToken } = response.data || {};

    if (access_token) localStorage.setItem('token', access_token);
    if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);

    return response;
  } finally {
    clearRefreshLock(owner);
  }
};

// ─── Refresh Queue (Singleton) ───────────────────────────────────────────────

let _isRefreshing = false;
let _queue        = []; // [{resolve, reject}]

const flushQueue = (accessToken = null, error = null) => {
  _queue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(accessToken)
  );
  _queue = [];
};

// ─── Configurable Callbacks ──────────────────────────────────────────────────
// Default: อ่านจาก localStorage + redirect ไป login
const _cb = {
  getRefreshToken:  () => localStorage.getItem('refreshToken'),
  onRefreshSuccess: (_tokens) => {},
  onForceLogout:    () => { window.location.href = '/login'; },
};

/**
 * เรียก 1 ครั้งตอน AuthProvider mount
 * เพื่อ inject callbacks จาก React context เข้ามา
 */
export const configureAuthClient = ({ getRefreshToken, onRefreshSuccess, onForceLogout }) => {
  if (getRefreshToken)  _cb.getRefreshToken  = getRefreshToken;
  if (onRefreshSuccess) _cb.onRefreshSuccess = onRefreshSuccess;
  if (onForceLogout)    _cb.onForceLogout    = onForceLogout;
};

// ─── Request Interceptor — แนบ Bearer token + ป้องกัน path ซ้ำ ──────────────────

const requestInterceptor = (config) => {
  // 1. แนบ Token
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;

  return config;
};

authClient.interceptors.request.use(requestInterceptor, (err) => Promise.reject(err));
apiClient.interceptors.request.use(requestInterceptor,  (err) => Promise.reject(err));

// ─── Response Interceptor (apiClient เท่านั้น) ───────────────────────────────
// authClient ไม่ต้องการ 401 retry เพราะ refresh endpoint ไม่ควร loop

apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const orig = error.config;

    // ไม่ใช่ 401 หรือ retry แล้ว → pass through
    if (error.response?.status !== 401 || orig._retry) {
      return Promise.reject(error);
    }
    
    // ถ้าโดนแบน / ปิดสิทธิ์ (ACCOUNT_DISABLED) ไม่ต้องไปขอ refresh ให้เตะออกเลย
    if (error.response?.data?.error_code === 'ACCOUNT_DISABLED') {
      _cb.onForceLogout('ACCOUNT_DISABLED');
      return Promise.reject(error);
    }

    // ไม่มี refresh token → force logout
    const rt = _cb.getRefreshToken();
    if (!rt) {
      _cb.onForceLogout();
      return Promise.reject(error);
    }

    // มีคนกำลัง refresh อยู่ → เข้า queue รอ
    if (_isRefreshing) {
      return new Promise((resolve, reject) => {
        const QUEUE_TIMEOUT = 10_000;
        const timer = setTimeout(
          () => reject(new Error('Refresh queue timeout')), QUEUE_TIMEOUT
        );
        _queue.push({
          resolve: (newToken) => {
            clearTimeout(timer);
            orig.headers.Authorization = `Bearer ${newToken}`;
            resolve(apiClient(orig));
          },
          reject: (err) => {
            clearTimeout(timer);
            reject(err);
          },
        });
      });
    }

    // คนแรกที่เจอ 401 → เป็นคน refresh
    orig._retry  = true;
    _isRefreshing = true;

    try {
      const { data } = await refreshWithCrossTabLock(rt);
      const { access_token, refresh_token: newRt, error_code } = data;

      if (error_code) throw Object.assign(new Error(data.message || 'Refresh failed'), { error_code });

      // บันทึก token ใหม่
      localStorage.setItem('token', access_token);
      if (newRt) localStorage.setItem('refreshToken', newRt);

      // อัพเดท default header
      apiClient.defaults.headers.Authorization = `Bearer ${access_token}`;

      // แจ้ง context อัพเดท state
      _cb.onRefreshSuccess({ access_token, refresh_token: newRt });

      // ปล่อย queue ที่รออยู่
      flushQueue(access_token);

      orig.headers.Authorization = `Bearer ${access_token}`;
      return apiClient(orig);

    } catch (err) {
      flushQueue(null, err);
      _cb.onForceLogout(err.error_code || null);
      return Promise.reject(err);

    } finally {
      _isRefreshing = false;
    }
  }
);
